import { Box, Card, ChevronRightIcon } from '@deckee/deck-hand';
import React, { FC, ReactNode, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import CollapseSlideTransition from '../../../../shared/CollapseSlideTransition/CollapseSlideTransition';
import {
  SLIDE_DURATION_MS,
  SLIDE_EASING,
} from '../../../../shared/slideTransition/slideTransition';
import type { WeatherForecastCardName } from '../../helpers/analytics/weatherForecastAnalytics';
import { useWeatherForecastCardExpanded } from '../../hooks/useWeatherForecastCardExpanded';
import SectionCard from './SectionCard';

const CollapsibleHeader = styled(Box)`
  cursor: pointer;
  user-select: none;
`;

const CollapsibleCardHeading = styled(Card.Heading).attrs({
  size: 'compact',
  mb: 0,
})<{ $muted?: boolean }>`
  font-weight: 700;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
  ${({ $muted, theme }) => ($muted ? `color: ${theme.colors.darkergrey};` : '')}
`;

const ChevronWrapper = styled(Box)<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  transition: transform ${SLIDE_DURATION_MS}ms ${SLIDE_EASING};
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`;

type CollapsibleCardSharedProps = {
  title: string;
  testId?: string;
  defaultExpanded?: boolean;
  mb?: number;
  children?: ReactNode;
  /** Muted titles read as a placeholder (loading/unavailable) rather than content. */
  titleTone?: 'default' | 'muted';
  titleTestId?: string;
  ariaBusy?: boolean;
  /**
   * ARIA role for the card itself, e.g. "alert" so an async failure is
   * announced without needing a MessageBlock body to focus.
   */
  cardRole?: string;
};

export type CollapsibleCardProps = CollapsibleCardSharedProps &
  (
    | {
        /** Collapsible cards must name themselves for toggle analytics + persistence. */
        collapsible?: true;
        analyticsCard: WeatherForecastCardName;
      }
    | {
        /** Static header — not toggleable, so no analytics card name. */
        collapsible: false;
        analyticsCard?: undefined;
      }
  );

const CollapsibleCard: FC<CollapsibleCardProps> = ({
  title,
  testId,
  analyticsCard,
  defaultExpanded = true,
  collapsible = true,
  mb = 3,
  children,
  titleTone = 'default',
  titleTestId,
  ariaBusy,
  cardRole,
}) => {
  const theme = useTheme();
  const { isExpanded, toggleExpanded } = useWeatherForecastCardExpanded(
    analyticsCard,
    defaultExpanded,
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleExpanded();
      }
    },
    [toggleExpanded],
  );

  if (!collapsible) {
    return (
      <SectionCard
        data-testid={testId}
        mb={mb}
        width="100%"
        aria-busy={ariaBusy}
        role={cardRole}
      >
        <Box display="flex" alignItems="center" py={3}>
          <Box flex="1 1 auto" minWidth={0}>
            <CollapsibleCardHeading
              $muted={titleTone === 'muted'}
              data-testid={titleTestId}
            >
              {title}
            </CollapsibleCardHeading>
          </Box>
        </Box>
        {children ? <Card.Body pt={0}>{children}</Card.Body> : null}
      </SectionCard>
    );
  }

  return (
    <SectionCard
      data-testid={testId}
      mb={mb}
      width="100%"
      aria-busy={ariaBusy}
      role={cardRole}
    >
      <CollapsibleHeader
        display="flex"
        alignItems="center"
        py={3}
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        data-testid={testId ? `${testId}-header` : undefined}
      >
        <Box flex="1 1 auto" minWidth={0}>
          <CollapsibleCardHeading
            $muted={titleTone === 'muted'}
            data-testid={titleTestId}
          >
            {title}
          </CollapsibleCardHeading>
        </Box>
        <Box flex="0 0 auto" display="flex" alignItems="center" pr={3} pl={1}>
          <ChevronWrapper $expanded={isExpanded}>
            <ChevronRightIcon color={theme.colors.darkgrey} size={20} />
          </ChevronWrapper>
        </Box>
      </CollapsibleHeader>

      <CollapseSlideTransition expanded={isExpanded}>
        <Card.Body pt={0}>{children}</Card.Body>
      </CollapseSlideTransition>
    </SectionCard>
  );
};

export default CollapsibleCard;
